// Programmatic SEO content: LinkedIn post ideas per topic/persona.
// Each entry renders at /post-ideas/<slug> via PostIdeasPage.jsx.
// Add a new topic here + a prerender entry + a sitemap entry to scale.

export const POST_IDEAS = [
  {
    slug: "devops-engineers",
    persona: "DevOps Engineers",
    title: "LinkedIn Post Ideas for DevOps Engineers",
    metaDescription:
      "10 LinkedIn post ideas for DevOps engineers — pipeline war stories, contrarian takes on platform teams, and hooks that work for a technical audience.",
    intro:
      "DevOps work is invisible when it goes well — which is exactly why it makes great LinkedIn content. Nobody posts about the deploy that didn't break, but everyone relates to it. The engineers who build audiences in this niche don't share vendor announcements; they share what actually happened: the migration, the incident, the pipeline that took three weeks longer than the estimate.",
    ideas: [
      {
        hook: "We cut deploy time from 45 minutes to 4. The fix wasn't what you think.",
        angle:
          "Case study format. Walk through the bottleneck you found (cache misses? test parallelization? image size?), what you tried first that failed, and the actual fix. Concrete numbers make it credible.",
      },
      {
        hook: "Your CI pipeline is slow because nobody owns it.",
        angle:
          "Contrarian take. Argue that pipeline speed is an ownership problem, not a tooling problem. Defend it with what happened when someone finally owned yours.",
      },
      {
        hook: "The most dangerous phrase in DevOps: 'it works in staging.'",
        angle:
          "Story post. One specific time staging lied to you, what was different in prod, and the config drift lesson you took away.",
      },
      {
        hook: "5 things I check before every production deploy (after 200+ deploys)",
        angle:
          "Listicle. Your actual pre-deploy checklist. The specificity of '200+ deploys' does the authority work for you.",
      },
      {
        hook: "We deleted 40% of our Terraform. Infrastructure got more reliable.",
        angle:
          "Counterintuitive result. Explain the over-abstraction problem — modules wrapping modules — and what ruthless simplification looked like.",
      },
      {
        hook: "GitOps sounds great until you're debugging why ArgoCD won't sync at 2am.",
        angle:
          "Honest-tradeoffs post. You still recommend GitOps, but list the operational sharp edges nobody mentions in conference talks.",
      },
      {
        hook: "Junior engineers ask me how to get into DevOps. Here's what I actually tell them.",
        angle:
          "Career post. Skip the certification list — share the one project type that teaches the real skills (run something in production, break it, fix it).",
      },
      {
        hook: "Our postmortem template has one section that changed everything: 'What went well.'",
        angle:
          "Process insight. How blameless postmortems actually get adopted, with one real incident where the template changed the conversation.",
      },
      {
        hook: "Kubernetes was the wrong choice for us. Here's how I'd decide today.",
        angle:
          "Decision-framework post. When k8s pays off vs when a simpler platform wins. Engineers love honest 'we over-engineered' stories.",
      },
      {
        hook: "The on-call rotation fix that cut our pages by 60%: we made the loudest alert a ticket.",
        angle:
          "Small-change-big-result. Alert fatigue is universally felt; share the triage rule you used to decide page vs ticket.",
      },
    ],
    tips: [
      "Lead with the number. '45 minutes to 4' in line one beats any clever opening.",
      "Name real tools (Terraform, ArgoCD, GitHub Actions) — it signals you've actually done the work and helps LinkedIn's algorithm match you to a technical audience.",
      "War stories outperform how-tos in this niche. The failure is the hook; the fix is the payoff.",
    ],
  },
  {
    slug: "sre",
    persona: "SREs",
    title: "LinkedIn Post Ideas for SREs & Observability Engineers",
    metaDescription:
      "10 LinkedIn post ideas for SREs — incident stories, SLO philosophy, alert fatigue, and observability takes that resonate with reliability engineers.",
    intro:
      "SRE content has a built-in advantage on LinkedIn: incidents are stories, and stories get read. But the niche is bigger than postmortems — SLO philosophy, toil budgets, and the eternal 'observability vs monitoring' debate all pull strong engagement from a technical audience. The key is writing from a specific incident or decision, not from theory.",
    ideas: [
      {
        hook: "Our uptime was 99.99%. Our users were still furious.",
        angle:
          "The classic SLI-mismatch story. Availability looked perfect but latency (or a broken critical path) made the product unusable. Argue for measuring what users feel.",
      },
      {
        hook: "The outage lasted 14 minutes. The retro took 3 weeks. Worth every hour.",
        angle:
          "Postmortem-culture post. What a deep retro surfaced that a quick one would have missed — usually an organizational cause behind the technical one.",
      },
      {
        hook: "We deleted half our dashboards and got better at debugging.",
        angle:
          "Contrarian observability take. Dashboard sprawl vs query-driven debugging. Name the moment you realized nobody looked at 80% of the panels.",
      },
      {
        hook: "Error budgets aren't a math exercise. They're a permission slip.",
        angle:
          "SLO philosophy. The real value of error budgets: giving teams explicit permission to ship (or explicit cover to stop and fix). One story where the budget settled an argument.",
      },
      {
        hook: "3am. Pager fires. The runbook is 2 years out of date. What we changed after that night.",
        angle:
          "Story + process fix. Runbook rot is universal. Share the mechanism you added (runbook review in the on-call handoff? runbooks as code?).",
      },
      {
        hook: "Stop alerting on causes. Alert on symptoms.",
        angle:
          "Opinionated principle post. CPU alerts vs 'users can't check out' alerts. Show one alert you rewrote and what the page rate did.",
      },
      {
        hook: "The most valuable SRE skill isn't technical. It's writing.",
        angle:
          "Career take. Postmortems, RFCs, status updates during incidents — the SREs who get promoted write clearly under pressure. Slightly contrarian, very shareable.",
      },
      {
        hook: "We measured our toil for one quarter. The number embarrassed everyone.",
        angle:
          "Data-backed post. How you measured it, what percentage came back, and which automation actually paid off first.",
      },
      {
        hook: "OpenTelemetry migration, 6 months in: what I'd tell my past self.",
        angle:
          "Migration retrospective. Honest costs (instrumentation debt, cardinality bills) alongside the payoff. Migration content ranks and gets saved.",
      },
      {
        hook: "Your incident severity levels are theater if SEV-1 doesn't wake anyone up.",
        angle:
          "Provocative process post. What each severity should mechanically trigger, and the incident that made you formalize it.",
      },
    ],
    tips: [
      "Anonymized incident stories are your highest-performing format. Timeline, wrong turn, fix, lesson.",
      "Take a side in live debates (dashboards vs queries, SLOs vs uptime) — neutral summaries don't get comments.",
      "Cardinality, toil, error budgets: niche vocabulary filters your audience to exactly the people who might hire you or buy from you.",
    ],
  },
  {
    slug: "ml-engineers",
    persona: "ML Engineers",
    title: "LinkedIn Post Ideas for ML & AI Engineers",
    metaDescription:
      "10 LinkedIn post ideas for ML and AI engineers — LLM production lessons, eval philosophy, cost stories, and takes that cut through AI hype.",
    intro:
      "AI content is everywhere on LinkedIn, and almost all of it is hype. That's your opening: engineers who post about what actually happens in production — eval failures, cost surprises, the gap between demo and deployment — stand out immediately against the flood of 'AI will change everything' posts. In this niche, specificity is the differentiator.",
    ideas: [
      {
        hook: "Our LLM demo took 2 days. Production took 6 months. Here's what filled the gap.",
        angle:
          "The demo-to-production gap is the most relatable story in AI right now. List what the demo hid: evals, edge cases, latency, cost, guardrails.",
      },
      {
        hook: "We cut our OpenAI bill by 70% without touching quality. Thread of what worked.",
        angle:
          "Cost-optimization case study. Caching, prompt compression, model routing, batching — with real percentages per technique.",
      },
      {
        hook: "Your RAG system isn't hallucinating. Your retrieval is failing silently.",
        angle:
          "Technical diagnosis post. Most 'hallucination' complaints trace to bad chunks. Show how you proved it (retrieval evals) and what fixed it.",
      },
      {
        hook: "Unpopular opinion: most companies don't need fine-tuning. They need better prompts and evals.",
        angle:
          "Contrarian take with a decision framework: when fine-tuning actually pays, and what to exhaust first. Invites strong comments from both camps.",
      },
      {
        hook: "We shipped an eval suite before we shipped the feature. Best decision of the quarter.",
        angle:
          "Process post. What your eval set contains, how it caught a regression that would have shipped, and how long it took to build (less than people fear).",
      },
      {
        hook: "The model wasn't the problem. The problem was we couldn't tell when it was wrong.",
        angle:
          "Observability-for-ML story. How you added confidence signals, human review queues, or drift detection after an embarrassing silent failure.",
      },
      {
        hook: "I reviewed 50 'AI engineer' resumes this month. Here's what actually stood out.",
        angle:
          "Hiring-side career post. Shipped projects with real users beat certificate lists. Specific and useful to the largest audience in this niche.",
      },
      {
        hook: "GPT-4 to a fine-tuned 7B model: our latency dropped 8x. Quality dropped 2%. Ship it.",
        angle:
          "Data-backed tradeoff post. The numbers are the hook; the decision framework (when small models win) is the payoff.",
      },
      {
        hook: "Prompt engineering is a real skill. It's just not the skill LinkedIn thinks it is.",
        angle:
          "Reframe post: it's systematic iteration against an eval set, not clever wording. Show one before/after prompt with measured results.",
      },
      {
        hook: "Six months of LLMs in production: the failure modes nobody blogs about.",
        angle:
          "Listicle of real failures — prompt injection attempts, token limit edge cases, provider outages, cost spikes from retry storms. Save-worthy content.",
      },
    ],
    tips: [
      "Anti-hype positioning wins. The audience is exhausted by breathless AI content — measured, evidence-based takes stand out.",
      "Include real numbers (latency, cost, eval scores). Nothing separates practitioners from commentators faster.",
      "Posts about failures outperform posts about successes roughly 2:1 in this niche — vulnerability plus competence is the winning combination.",
    ],
  },
  {
    slug: "cloud-engineers",
    persona: "Cloud Engineers",
    title: "LinkedIn Post Ideas for Cloud Engineers & Architects",
    metaDescription:
      "10 LinkedIn post ideas for cloud engineers — cost-optimization wins, migration war stories, multi-cloud takes, and architecture decisions that resonate.",
    intro:
      "Cloud content has one guaranteed hit: the bill. Cost stories travel further than any architecture diagram because every engineering leader feels the pain. Beyond FinOps, migration retrospectives and honest 'we chose boring technology' posts consistently outperform vendor-flavored content in this niche.",
    ideas: [
      {
        hook: "We cut our AWS bill by $40k/month. The biggest win took 20 minutes.",
        angle:
          "FinOps case study. Rank your savings by effort-to-impact (the 20-minute win is usually unattached EBS volumes, oversized instances, or a forgotten environment). Real numbers required.",
      },
      {
        hook: "Multi-cloud is a strategy on slides and a tax in production.",
        angle:
          "Contrarian take. The real costs: duplicated expertise, lowest-common-denominator architecture, tooling sprawl. Note the exceptions (regulatory, M&A) to keep it honest.",
      },
      {
        hook: "Our 200GB Postgres migration to Aurora: zero downtime, three near-disasters.",
        angle:
          "Migration war story. The plan, the two things that almost went wrong, and the rollback plan you never used but that let everyone sleep.",
      },
      {
        hook: "Serverless didn't reduce our ops burden. It relocated it.",
        angle:
          "Honest-tradeoffs post. Cold starts, debugging distributed traces, IAM sprawl. You'd still choose it for the right workloads — say which ones.",
      },
      {
        hook: "The staging environment that cost more than production. A post about cloud waste.",
        angle:
          "Confession-format post. How it happened gradually, how you found it, and the tagging/budget-alert setup that prevents a repeat.",
      },
      {
        hook: "Stop designing for scale you don't have.",
        angle:
          "Architecture philosophy. The startup that built for 100x traffic and got complexity instead. What 'boring until it hurts' looks like in practice.",
      },
      {
        hook: "5 AWS services I'd learn first if I started over in 2026",
        angle:
          "Listicle for career-changers. Your actual shortlist with one sentence on why each earns its place. Broad reach, easy saves.",
      },
      {
        hook: "We went back from microservices to a modular monolith. Latency, costs, and sanity all improved.",
        angle:
          "Counterintuitive architecture story. The coordination costs that drove the decision and the numbers after consolidation.",
      },
      {
        hook: "Your DR plan is a document. Ours failed the first real test. Here's what a working one looks like.",
        angle:
          "Disaster-recovery honesty. The gap between the runbook and reality, and the game-day practice that closed it.",
      },
      {
        hook: "IAM is the hardest part of the cloud and nobody wants to admit it.",
        angle:
          "Validation post. Engineers deeply feel this. Share your one structural rule (permission boundaries? centralized roles module?) that made it manageable.",
      },
    ],
    tips: [
      "Dollar figures are the strongest hooks in cloud content — '$40k/month' outperforms 'significant savings' every time.",
      "Migration retrospectives get saved and searched. Structure them: context, plan, surprise, outcome, what you'd change.",
      "Avoid vendor cheerleading. 'It depends, here's the framework' positions you as the architect, not the fan.",
    ],
  },
  {
    slug: "data-scientists",
    persona: "Data Scientists",
    title: "LinkedIn Post Ideas for Data Scientists",
    metaDescription:
      "10 LinkedIn post ideas for data scientists — model-in-production stories, stakeholder communication, metric pitfalls, and career takes that resonate.",
    intro:
      "The best data science content on LinkedIn isn't about models — it's about the gap between analysis and impact. Posts about stakeholder communication, metrics that mislead, and models that never shipped consistently outperform technical tutorials, because they speak to what actually makes the job hard.",
    ideas: [
      {
        hook: "My most impactful analysis was a SQL query and a bar chart. My least impactful was a neural network.",
        angle:
          "Impact-versus-complexity story. The business context that made the simple thing valuable. Deeply relatable to every DS who's over-engineered.",
      },
      {
        hook: "The model was 94% accurate. It was also useless. A story about the wrong metric.",
        angle:
          "Metric-mismatch case study. Class imbalance, or optimizing for accuracy when the business needed recall. Show the metric that actually mattered.",
      },
      {
        hook: "80% of my job is convincing people the data doesn't say what they want it to say.",
        angle:
          "Stakeholder-reality post. One (anonymized) story of a leader with a conclusion looking for evidence, and the technique you used to reframe it.",
      },
      {
        hook: "Our A/B test showed +12% conversion. We shipped it. Revenue dropped. Here's what we missed.",
        angle:
          "Cautionary experimentation tale — novelty effects, cannibalization, or a segment mix shift. Data leaders love sharing these.",
      },
      {
        hook: "Stop putting 'proficient in TensorFlow' on your resume if you've never deployed a model.",
        angle:
          "Blunt career advice. What hiring managers actually probe for. Framed to help juniors, sharp enough to get shared.",
      },
      {
        hook: "The dashboard nobody opened: a post about building analytics people actually use.",
        angle:
          "Confession + fix. The requirements-gathering mistake, and how sitting with users for one day changed what you built.",
      },
      {
        hook: "Data quality is not a pipeline problem. It's an incentives problem.",
        angle:
          "Systems-thinking take. Bad data comes from teams with no reason to care. What changed when data quality showed up in someone's goals.",
      },
      {
        hook: "I spent 3 weeks feature engineering. The baseline model won anyway.",
        angle:
          "Humility post with a lesson: always establish the dumb baseline first. Engineers and DS both love this one.",
      },
      {
        hook: "Notebooks are where analysis goes to die. Here's our path from notebook to production.",
        angle:
          "Process post. Your actual promotion path (notebook → module → pipeline → monitoring) with the friction points you removed.",
      },
      {
        hook: "'Just make it a dashboard' is how good questions get bad answers.",
        angle:
          "Opinionated take on decision-support vs data-display. When a memo with a recommendation beats a self-serve dashboard.",
      },
    ],
    tips: [
      "Stories about communication failures outperform stories about model failures — the audience feels them more.",
      "Write the business outcome first, method second. 'Revenue dropped' hooks; 'we used CUPED' doesn't.",
      "Beginner-mistake content has the widest reach; production-ML content has the highest-value audience. Alternate between them.",
    ],
  },
  {
    slug: "engineering-managers",
    persona: "Engineering Managers",
    title: "LinkedIn Post Ideas for Engineering Managers & Directors",
    metaDescription:
      "10 LinkedIn post ideas for engineering managers — 1:1 frameworks, promotion honesty, org design lessons, and leadership content that builds credibility.",
    intro:
      "Engineering leadership is the highest-leverage niche on LinkedIn: the audience includes your future hires, your peers, and the people who might hire you. The content that works is specific and slightly vulnerable — real decisions, real mistakes, real frameworks — not recycled leadership platitudes. One concrete story about a hard conversation beats ten posts about 'servant leadership.'",
    ideas: [
      {
        hook: "I promoted the wrong person. It took me a year to understand why.",
        angle:
          "Vulnerable leadership story. The visible-work bias (or interview-skills bias) that drove the call, the cost, and how your promotion criteria changed.",
      },
      {
        hook: "My best engineer told me he was bored. What I did in the next 48 hours mattered more than his last review.",
        angle:
          "Retention story. The specific conversation and the project/scope change that kept them. EMs save this kind of post.",
      },
      {
        hook: "We doubled headcount and shipped less. The math nobody does before scaling.",
        angle:
          "Org-design post. Coordination costs, onboarding drag, decision bottlenecks. One structural change that restored throughput.",
      },
      {
        hook: "The 3 questions I ask in every 1:1 (and the one I stopped asking)",
        angle:
          "Framework listicle. Your actual questions with the reasoning. The 'stopped asking' element makes it a story, not a template.",
      },
      {
        hook: "Your team doesn't need more process. It needs fewer decisions per week.",
        angle:
          "Contrarian ops take. Decision fatigue vs process overhead — what you defaulted, delegated, or deleted, and what it freed up.",
      },
      {
        hook: "I fired someone I liked. Every manager eventually writes this post — here's mine.",
        angle:
          "The hardest-conversation post. Focus on the months of avoidance before, not the termination itself. What you'd do sooner.",
      },
      {
        hook: "Managers who code: I read every PR for 30 minutes a day. Here's what it changes.",
        angle:
          "Take a side in the eternal debate. Your specific practice, what signal it gives you, and where the line is (you don't block releases).",
      },
      {
        hook: "The best performance review I ever gave had zero surprises in it.",
        angle:
          "Feedback-philosophy post. If the review surprises anyone, the manager failed months earlier. Your cadence for making feedback boring.",
      },
      {
        hook: "Exit interviews are too late. Here's the question I ask every quarter instead.",
        angle:
          "Stay-interview framework. The actual question, one answer that surprised you, and the retention save it enabled.",
      },
      {
        hook: "I track one metric for team health. It's not velocity.",
        angle:
          "Curiosity-gap hook. Whatever yours is (cycle time? PR review latency? Friday deploy confidence?), defend it against the obvious alternatives.",
      },
    ],
    tips: [
      "Vulnerability with a lesson is the winning format — a mistake you owned outperforms advice you dispensed.",
      "Never make a current team member identifiable, even positively. Time-shift and anonymize every story.",
      "First-person singular hooks ('I promoted...', 'I fired...') dramatically outperform 'we' or abstract openings in leadership content.",
    ],
  },
  {
    slug: "product-managers",
    persona: "Technical Product Managers",
    title: "LinkedIn Post Ideas for Technical Product Managers",
    metaDescription:
      "10 LinkedIn post ideas for technical PMs — roadmap honesty, engineer-PM collaboration, discovery lessons, and metric takes that build product credibility.",
    intro:
      "Technical PM content lives in the gap between what stakeholders ask for and what actually ships. The posts that perform are honest about the messy middle: killed features, roadmaps that were fiction, metrics that lied. That honesty is rare in a niche full of framework-recycling — which is exactly why it works.",
    ideas: [
      {
        hook: "We killed a feature 6 weeks before launch. Best product decision of the year.",
        angle:
          "Sunk-cost story. The signal that triggered the kill, the meeting where you made the call, and what shipped instead with those engineers.",
      },
      {
        hook: "Your roadmap is a work of fiction. Everyone knows it. Say it out loud.",
        angle:
          "Contrarian process take. Argue for Now/Next/Later over date-based theater, and describe the stakeholder conversation that gets you there.",
      },
      {
        hook: "I shadowed our support team for a day. It rewrote my quarter's priorities.",
        angle:
          "Discovery story. Three specific things you heard that no dashboard showed. Ends with a habit: one support day per quarter.",
      },
      {
        hook: "The feature had 40% adoption. Users hated it. A post about vanity metrics.",
        angle:
          "Metric-honesty case study. Adoption vs satisfaction vs retention — the metric that finally told the truth and what you did about it.",
      },
      {
        hook: "Engineers don't hate meetings. They hate meetings where nothing gets decided.",
        angle:
          "PM-eng collaboration post. Your rule for meetings with engineers (every meeting ends in a decision or an owner). Wins you credibility with both audiences.",
      },
      {
        hook: "'Can we just add a toggle?' — how settings sprawl killed our UX, and the rule that stopped it.",
        angle:
          "Product-craft post. The real cost of configurability and your bar for adding an option (who maintains it? who tests it?).",
      },
      {
        hook: "I was the PM who over-promised. Here's the sprint that cured me.",
        angle:
          "Vulnerable story. The commitment you made without asking engineering, the crunch it caused, and your estimation rule now.",
      },
      {
        hook: "Users asked for a faster horse. We almost built the stable. What discovery actually looks like.",
        angle:
          "Jobs-to-be-done in practice: the feature request, the underlying job you uncovered in interviews, and the much smaller thing you shipped.",
      },
      {
        hook: "The best PRD I ever wrote was one page. The worst was fourteen.",
        angle:
          "Documentation take. What actually needs to be written down (problem, non-goals, success metric) vs what's spec theater.",
      },
      {
        hook: "Technical debt is a product decision. Stop letting engineering own it alone.",
        angle:
          "Bridge-building post. How you budget debt work into the roadmap (a fixed percentage? a debt sprint?) and what it bought you in velocity.",
      },
    ],
    tips: [
      "Stories about being wrong build more trust than frameworks about being right — and PMs live on trust.",
      "Write for engineers as much as for PMs. 'PM who gets it' is the strongest personal brand in this niche.",
      "End posts with a question ('How does your team handle this?') — PM audiences comment more than any other tech niche.",
    ],
  },
];

export const getPostIdeasBySlug = (slug) => POST_IDEAS.find((t) => t.slug === slug);
